import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DisclaimerV1 from "@/components/DisclaimerV1";
import apiClient from "@/lib/apiClient";
import {
  ArrowLeft, Upload, PenLine, ChevronRight,
  FileText, Calendar, Pill, AlertTriangle,
  Loader2, User, MoreHorizontal, CheckCircle
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric", month: "long", year: "numeric",
  });
}

function sexLabel(sex) {
  if (!sex) return null;
  if (sex === "M" || sex === "male" || sex === "homme") return "Homme";
  if (sex === "F" || sex === "female" || sex === "femme") return "Femme";
  return sex;
}

function AnalysisRow({ analysis }) {
  const navigate = useNavigate();
  const isManual = !analysis.pdf_filename && !analysis.pdf_path;
  const medsCount = analysis.medications?.length ?? analysis.patient_data?.medications?.length ?? 0;
  const stoppCount = analysis.stopp_alerts?.length ?? 0;

  return (
    <button
      onClick={() => navigate(`/analysis/${analysis.id}`)}
      className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left group border-b border-slate-100 last:border-0"
    >
      {/* Icône type */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isManual ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600"
      }`}>
        {isManual ? <PenLine className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      </div>

      {/* Date + type */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800">
          {formatDate(analysis.created_at || analysis.timestamp)}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">
          {isManual ? "Saisie manuelle" : "Import PDF"}
          {medsCount > 0 && ` · ${medsCount} médicament${medsCount > 1 ? "s" : ""}`}
        </p>
      </div>

      {/* Badge STOPP */}
      {stoppCount > 0 && (
        <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full flex-shrink-0">
          <AlertTriangle className="h-3 w-3" />
          {stoppCount} STOPP
        </span>
      )}

      {/* Statut */}
      {analysis.validated && (
        <span className="flex items-center gap-1 text-xs text-emerald-600 flex-shrink-0">
          <CheckCircle className="h-3 w-3" />
          Validée
        </span>
      )}

      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-blue-400 flex-shrink-0 transition-colors" />
    </button>
  );
}

export default function PatientView() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loadingPatient, setLoadingPatient] = useState(true);
  const [loadingAnalyses, setLoadingAnalyses] = useState(true);
  const [error, setError] = useState("");

  const fetchPatient = useCallback(async () => {
    try {
      const res = await apiClient.get(`/patients/${patientId}`);
      setPatient(res.data);
    } catch {
      setError("Patient introuvable.");
    } finally {
      setLoadingPatient(false);
    }
  }, [patientId]);

  const fetchAnalyses = useCallback(async () => {
    try {
      const res = await apiClient.get("/analyses/");
      const all = res.data || [];
      // Filtre par patient_id
      const mine = all.filter(
        (a) => a.patient_id === patientId || a.patientId === patientId
      );
      // Tri par date desc
      mine.sort((a, b) =>
        new Date(b.created_at || b.timestamp || 0) -
        new Date(a.created_at || a.timestamp || 0)
      );
      setAnalyses(mine);
    } catch {
      setAnalyses([]);
    } finally {
      setLoadingAnalyses(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchPatient();
    fetchAnalyses();
  }, [fetchPatient, fetchAnalyses]);

  const loading = loadingPatient || loadingAnalyses;

  // ─── Redirect ─────────────────────────────────────────────────────────────────
  const goImport = () =>
    navigate(`/analysis/compose${patientId ? `?patientId=${patientId}` : ""}`);
  const goManual = () =>
    navigate(`/analysis/compose${patientId ? `?patientId=${patientId}` : ""}`);

  if (loadingPatient) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Chargement...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error}
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-sm text-blue-600 hover:underline"
        >
          ← Retour à la liste
        </button>
      </div>
    );
  }

  const patientName =
    [patient?.first_name, patient?.last_name].filter(Boolean).join(" ") ||
    "Patient sans nom";

  const meta = [
    patient?.age ? `${patient.age} ans` : null,
    sexLabel(patient?.sex),
  ]
    .filter(Boolean)
    .join(" · ");

  const hasAnalyses = analyses.length > 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

      {/* Fil d'Ariane */}
      <div className="flex items-center gap-1 text-xs text-slate-400">
        <Link to="/" className="hover:text-blue-600 transition-colors">
          Mes patients
        </Link>
        <span>/</span>
        <span className="text-slate-600 font-medium truncate">{patientName}</span>
      </div>

      {/* Header patient */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-base font-bold flex-shrink-0">
          {[patient?.first_name, patient?.last_name]
            .filter(Boolean)
            .map((s) => s[0].toUpperCase())
            .join("") || <User className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-slate-800">{patientName}</h1>
          {meta && <p className="text-sm text-slate-400 mt-0.5">{meta}</p>}
          {patient?.notes && (
            <p className="text-xs text-slate-500 mt-1 italic">{patient.notes}</p>
          )}
        </div>
      </div>

      {/* Séparateur */}
      <hr className="border-slate-100" />

      {/* CTAs — proéminents si pas d'analyses, compacts sinon */}
      {!hasAnalyses ? (
        /* Empty state avec grandes CTAs */
        <div className="space-y-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Nouvelle révision
          </p>
          <p className="text-sm text-slate-400">
            Aucune analyse pour ce patient. Commencez par importer les documents
            ou saisir les données manuellement.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button
              onClick={goImport}
              className="flex flex-col gap-2 p-5 border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 rounded-xl transition-colors text-left group"
            >
              <Upload className="h-6 w-6 text-blue-500 group-hover:text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  Importer des documents
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Ordonnance, CRH, lettres PDF
                </p>
              </div>
            </button>

            <button
              onClick={goManual}
              className="flex flex-col gap-2 p-5 border-2 border-slate-200 hover:border-amber-300 hover:bg-amber-50/40 rounded-xl transition-colors text-left group"
            >
              <PenLine className="h-6 w-6 text-amber-500 group-hover:text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-slate-700">
                  Saisie manuelle
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Médicaments et antécédents
                </p>
              </div>
            </button>
          </div>
        </div>
      ) : (
        /* CTAs compactes si analyses présentes */
        <div className="flex gap-2">
          <button
            onClick={goImport}
            className="flex items-center gap-2 border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Upload className="h-4 w-4 text-blue-500" />
            Importer des documents
          </button>
          <button
            onClick={goManual}
            className="flex items-center gap-2 border border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <PenLine className="h-4 w-4 text-amber-500" />
            Saisie manuelle
          </button>
        </div>
      )}

      {/* Historique des analyses */}
      {hasAnalyses && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Analyses précédentes · {analyses.length}
          </p>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loadingAnalyses ? (
              <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-xs">Chargement...</span>
              </div>
            ) : (
              analyses.map((a) => <AnalysisRow key={a.id} analysis={a} />)
            )}
          </div>
        </div>
      )}

      <DisclaimerV1 />
    </div>
  );
}
